<template>
	<div>
		<div class="stage">
			<page-header title="Resellers" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="true"
				show-add-button
				add-button-label="Add Reseller"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@export="exportToCsv"
				@add-new="routeTo('/resellers/new')"
			/>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchField"
				:dates="dates"
				date-label="Date Added"
				show-date-filter
				show-search-filter
				search-label="Company Name"
				@update:filters="searchParams = $event"
				@update:search-term="searchField = $event"
				@update:dates="dates = $event"
			/>

			<data-view
				:view-mode="'table'"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Resellers'"
				:total-items="totalResellers"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="pages"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="resellers"
						:columns="columns"
						item-type="resellers"
						base-route="resellers"
						:can-edit="$global.hasUserRole(userRoles, 'resellers:update')"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-building',
							title: 'No resellers yet',
							description: 'Create your first reseller to start managing sub-tenants.',
							action: $global.hasUserRole(userRoles, 'resellers:create')
								? {
										label: 'Add Reseller',
										icon: 'fa-light fa-plus',
										handler: () => routeTo('/resellers/new')
									}
								: null
						}"
					>
					</generic-table>
				</template>
			</data-view>
		</div>
	</div>
</template>

<script>
import resellersApi from '../scripts/resellers/resellers.api'
import { router } from '../router'
import moment from 'moment'
import debounce from 'lodash.debounce'
import axios from 'axios'

export default {
	inject: ['user', 'userRoles'],
	data() {
		return {
			columns: [
				{
					key: 'companyName',
					label: 'Company Name',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-3',
					mobileLabel: 'Company'
				},
				{
					key: 'contactName',
					label: 'Contact',
					class: 'col-xs-12 col-lg-2 hideTabletLandscape',
					mobileLabel: 'Contact'
				},
				{
					key: 'email',
					label: 'Email',
					class: 'col-xs-12 col-lg-3 hideTabletLandscape',
					mobileLabel: 'Email'
				},
				{
					key: 'phone',
					label: 'Phone',
					class: 'col-xs-12 col-md-3 col-lg-2 hideTablet',
					mobileLabel: 'Phone'
				},
				{
					key: 'createdAt',
					label: 'Added',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2',
					mobileLabel: 'Created'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null
			},
			resellers: [],
			totalResellers: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'companyName',
			reverse: false,
			loading: false,
			searchField: '',
			dates: []
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalResellers / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		}
	},

	watch: {
		dates: {
			handler(val) {
				if (val?.length === 2) {
					this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
				} else {
					this.searchParams.dateAddedFrom = null
					this.searchParams.dateAddedTo = null
				}
			},
			deep: true
		},
		searchParams: {
			handler() {
				this.reloadResellers()
			},
			deep: true
		},
		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},
		currentPage() {
			this.reloadResellers()
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) {
			this.searchParams.selectedTenant = this.$global.getSelectedTenant()
		}

		this.debouncedWatch = debounce(this.reloadResellers, 1000)

		if (this.$route.query?.globalSearch) {
			this.reloadResellers(this.$route.query.globalSearch)
		} else {
			this.reloadResellers()
		}
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
	},

	methods: {
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null
			}
			this.dates = []
			this.searchField = ''
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams.dateAddedFrom) {
				params.where.createdAtFrom = `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? `${moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
					: `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
			}

			return params
		},

		async reloadResellers(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await resellersApi.getAll(params)
				this.totalResellers = response.data.count
				this.resellers = response.data.results

				const maxPages = Math.ceil(this.totalResellers / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadResellers(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		changePage(page) {
			this.currentPage = page
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalResellers / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.reloadResellers()
		},

		exportToCsv() {
			let searchParams = this.getSearchParams()
			searchParams.exportCsv = true

			axios
				.get(window.dtServerUrl + '/resellers', {
					params: searchParams
				})
				.then((response) => {
					let blob = new Blob([response.data], { type: response.headers.get('content-type') })
					let link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = 'teleflow-resellers.csv'
					link.click()
				})
				.catch(console.error)
		},

		setSort(column, reverse) {
			if (this.sortColumn !== column) {
				this.reverse = false
			} else {
				this.reverse = reverse
			}
			this.sortColumn = column
			this.reloadResellers()
		}
	}
}
</script>
